<script lang="ts" setup>
import { computed, ref, defineEmits } from "vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";
import BaseIcon from "@/components/base/BaseIcon.vue";
import { useI18n } from "vue-i18n";
import { useStore } from "@/store";

const store = useStore();
const emit = defineEmits(["action"]);

const { t } = useI18n();

const fieldState = ref<"inactive" | "active" | "error" | "applied">("inactive");
const errorText = ref<string>("");
const promoValue = ref<string>("");

const openInput = () => {
  if (fieldState.value === "inactive") {
    fieldState.value = "active";
  }
};

const watchInput = (e: Event) => {
  if (e.target?.value.length && fieldState.value === "error") {
    fieldState.value = "active";
  }
  let value = e.target?.value;
  value = value.replace(/[^a-zA-Z0-9\u0400-\u04FF-]/g, "");
  promoValue.value = value;
};

const applyCode = async () => {
  await store
    .dispatch("tariff/applyPromo", promoValue.value.toUpperCase())
    .then((res) => {
      console.log(res);
      fieldState.value = "applied";
      emit("action");
    })
    .catch((err) => {
      console.log(err);
      fieldState.value = "error";
      switch (err.response.data.code) {
        case 63:
          errorText.value = t("base.promocode.notFound");
          break;
        case 64:
          errorText.value = t("base.promocode.cannotUse");
          break;
        case 65:
          errorText.value = t("base.promocode.alreadyUsed");
          break;
        default:
          errorText.value = t("base.promocode.genericError");
      }
    });
};

const clearCode = () => {
  promoValue.value = "";
  fieldState.value = "active";
};

const classes = computed(() => {
  return {
    inactive: fieldState.value === "inactive",
    active: fieldState.value === "active",
    error: fieldState.value === "error",
    applied: fieldState.value === "applied",
  };
});
</script>

<template>
  <div class="promo">
    <div class="promo-wrapper" @click="openInput" :class="classes">
      <div class="promo-pic">
        <BaseFigureForIcon
          v-if="fieldState === 'applied'"
          class="promo-pic__success"
          icon="check-success"
          color="customer"
          size="small"
        />
        <div v-else class="promo-pic__flyer">
          <BaseIcon name="promo" />
        </div>
      </div>
      <div
        v-if="fieldState === 'inactive' || fieldState === 'applied'"
        class="promo-placeholder"
      >
        <p v-if="fieldState === 'inactive'" class="caption-medium thick">
          {{ t("base.promocode.hasPromocode") }}
        </p>
        <p
          v-if="fieldState === 'applied'"
          class="caption-medium thick uppercase"
        >
          {{ promoValue }}
        </p>
      </div>
      <div
        v-if="fieldState === 'active' || fieldState === 'error'"
        class="promo-input"
      >
        <input
          v-model="promoValue"
          type="text"
          @input="watchInput"
          :placeholder="t('base.promocode.placeholder')"
          minlength="4"
          maxlength="20"
          class="caption-medium thick"
        />
      </div>
      <div v-if="fieldState === 'active'" class="promo-apply">
        <BaseButton
          class="desktop"
          :title="t('base.promocode.apply')"
          size="small"
          :disabled="promoValue.length < 4"
          @click="applyCode"
        />
        <BaseFigureForIcon
          class="mobile"
          icon="arrow"
          color="primary"
          :disabled="promoValue.length === 0"
          @click="applyCode"
          is-button
        />
      </div>
      <div v-if="fieldState === 'error'" class="promo-error">
        <div v-if="fieldState === 'error'" class="promo-error--text desktop">
          <p class="caption-medium color-error">{{ errorText }}</p>
        </div>
        <div class="promo-error--close" @click="clearCode">
          <BaseIcon name="close" />
        </div>
      </div>
      <div
        v-if="fieldState === 'applied'"
        class="promo-cancel color-secondary"
        @click="clearCode"
      >
        <BaseIcon name="close" />
      </div>
    </div>
    <div class="mobile">
      <div v-if="fieldState === 'error'" class="promo-note">
        <p class="caption-medium color-error">{{ errorText }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/colors";

.promo {
  display: flex;
  flex-flow: column;
  gap: 8px;

  &-wrapper {
    display: flex;
    align-items: center;
    padding: 12px 15px;
    border-radius: 24px;
    background: var(--color-white);
    height: 72px;
    transition: background-color 0.4s;

    &.inactive {
      cursor: pointer;
    }

    &.active {
      background: $transparent-grey;
    }

    &.error {
      background: $transparent-grey;
      border: 1px solid var(--color-danger-primary);

      input {
        color: var(--color-danger-primary);
      }
    }
  }

  &-pic {
    margin-right: 12px;
    &__flyer {
      color: var(--color-secondary);
    }
    &__success {
      background-color: #007e00;
      color: var(--color-white);
    }
  }

  &-input {
    flex: 1;

    input {
      width: 100%;
      text-transform: uppercase;
      caret-color: var(--color-secondary);

      &::placeholder {
        text-transform: none;
        color: var(--color-grey-light);
      }
    }
  }

  &-apply {
    margin-left: auto;

    .desktop {
      display: block;
    }

    .mobile {
      display: none;
    }

    @media (max-width: 768px) {
      .desktop {
        display: none;
      }
      .mobile {
        display: block;
      }
    }
  }

  &-error {
    display: flex;
    align-items: center;
    margin-left: auto;
    gap: 19px;

    .desktop {
      display: block;
    }

    @media (max-width: 768px) {
      .desktop {
        display: none;
      }
    }

    &--close {
      padding: 5px 10px 0 0;
      cursor: pointer;
      color: var(--color-danger-primary);
    }
  }

  &-cancel {
    margin-left: auto;
    padding: 5px 10px 0 0;
    cursor: pointer;
  }

  .mobile {
    display: none;

    @media (max-width: 768px) {
      display: block;
    }
  }
}
</style>
